import { useCallback, useEffect, useState, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { MenuItem, SelectChangeEvent } from "@mui/material"
import classNames from "classnames"
import moment from "moment"

import { createDrawerStore } from "@/stores/global"
import Selector from "@/components/form/selector/Selector"
import Tab from "@/components/tab/Tab"
import { colors } from "@/components/colors"
import Chip from "@/components/chip/Chip"
import Checkbox from "@/components/form/checkbox/Checkbox"
import { getSingleItemDuration, getWholeDuration } from "@/utils/format"
import { TIME_FORMAT } from "@/constants/date"
import { ActivityDetail } from "@/types/activity"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { CheckboxColor } from "@/types/components/checkbox"

export default function SessionSelector({
  open,
  setOpen,
  selectedType,
  setSelectedType,
  selectedSessionList,
  setSelectedSessionList,
  detail,
  hideRadioSelection = false,
  disabled = false,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  selectedType: ACTIVITY_SESSION_TYPE
  setSelectedType: (selectedType: "activity" | "session") => void
  selectedSessionList: number[]
  setSelectedSessionList: (selectedSessionList: number[]) => void
  detail?: ActivityDetail
  hideRadioSelection?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const { isMobile } = createDrawerStore()

  const [tempSelectedSessionList, setTempSelectedSessionList] = useState<number[]>([])

  useEffect(() => {
    setTempSelectedSessionList(selectedSessionList)
  }, [selectedSessionList])

  // Tab 모드: 외부 클릭 시 닫기
  useEffect(() => {
    if (!open || hideRadioSelection) return

    const handleClickOutside = (e: MouseEvent) => {
      const menu = document.querySelector(".report-session-filter-menu")
      if (menu?.contains(e.target as Node) || containerRef.current?.contains(e.target as Node)) {
        return
      }
      setSelectedSessionList(tempSelectedSessionList)
      setOpen(false)
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open, hideRadioSelection, tempSelectedSessionList])

  const renderRadioLabel = useCallback(
    (value: string) => {
      return (
        <div className="session-radio-label">
          <p>{t("allSessions")}</p>
          <p>{detail && getWholeDuration(value, detail)}</p>
        </div>
      )
    },
    [detail],
  )

  const handleSelectAllSessions = (checked: boolean) => {
    if (checked && detail?.sessions) {
      setTempSelectedSessionList(detail.sessions.map((s) => s.idfSession))
    } else {
      setTempSelectedSessionList([])
    }
  }

  const isAllSessionsSelected = useMemo(() => {
    return (
      (detail?.sessions?.length ?? 0) > 0 &&
      tempSelectedSessionList.length === detail!.sessions.length
    )
  }, [detail?.sessions, tempSelectedSessionList])

  const handleSessionClick = (e: React.MouseEvent, sessionId: number) => {
    setOpen(true)
    e.preventDefault()
    e.stopPropagation()
    if (tempSelectedSessionList.includes(sessionId)) {
      setTempSelectedSessionList(tempSelectedSessionList.filter((id) => id !== sessionId))
    } else {
      setTempSelectedSessionList([...tempSelectedSessionList, sessionId])
    }
  }

  const renderSessionCheckboxList = (isDisabled: boolean) => (
    <div
      className={classNames("report-session-filter-selector-checkbox", {
        disabled: isDisabled,
      })}
      style={{ maxHeight: "400px", overflowY: "auto" }}>
      {detail?.sessions.map((session) => (
        <MenuItem
          component="div"
          key={session.idfSession}
          className="session-checkbox-item"
          onClick={(e) => {
            if (isDisabled) return
            handleSessionClick(e, session.idfSession)
          }}>
          <Checkbox
            value={session.idfSession}
            checked={tempSelectedSessionList.includes(session.idfSession)}
            disabled={isDisabled}
            onClick={(e) => handleSessionClick(e, session.idfSession)}
            color={CheckboxColor.PLATINUM}
          />
          <div className="session-checkbox-text">
            <div className="session-checkbox-name-wrapper">
              <p className="session-checkbox-name">{session.name}</p>
              <p className="session-checkbox-duration">{getSingleItemDuration(session)}</p>
            </div>
            <p className="session-checkbox-time">
              {moment(session.dateStart).format(TIME_FORMAT)} ~{" "}
              {moment(session.dateFinish).format(TIME_FORMAT)}
            </p>
          </div>
        </MenuItem>
      ))}
    </div>
  )

  return (
    <div className="report-session-filter" ref={containerRef}>
      <Selector
        type="select"
        open={open}
        onOpen={() => !disabled && setOpen(true)}
        onClose={() => {
          if (!hideRadioSelection) return
          setSelectedSessionList(tempSelectedSessionList)
          setOpen(false)
        }}
        MenuProps={{
          disablePortal: false,
          PaperProps: {
            className: "report-session-filter-menu",
            style: {
              width: containerRef.current?.offsetWidth,
              ...(isMobile && { minWidth: 280 }),
            },
          },
        }}
        displayEmpty
        value={hideRadioSelection ? selectedType : "_"}
        onChange={(e: SelectChangeEvent<string>) => {
          if (hideRadioSelection && !disabled) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(true)
            setSelectedType(e.target.value as "activity" | "session")
          }
        }}
        sx={{
          pointerEvents: disabled ? "none" : "auto",
          "& .MuiOutlinedInput-root": {
            opacity: 1,
            backgroundColor: "transparent",
            "&.Mui-disabled": {
              opacity: 1,
              backgroundColor: "transparent",
            },
            "& fieldset": {
              borderColor: disabled ? colors.neutral.grey["300"] : undefined,
            },
          },
          "& .MuiSelect-select": {
            color: disabled ? undefined : undefined,
            cursor: disabled ? "default" : "pointer",
          },
        }}
        renderValue={() => {
          if (selectedType === "activity" || !detail?.sessions?.length) {
            return t("activity")
          }

          return (
            <span className="report-filter-selector-value">
              {t("session")}
              <Chip type="number" label={selectedSessionList.length + ""} />
            </span>
          )
        }}>
        {!hideRadioSelection && (
          <div
            className="report-session-filter-tab-wrapper"
            onMouseDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}>
            <Tab
              type="filled"
              list={[
                { value: "activity", label: t("activity") },
                { value: "session", label: t("session") },
              ]}
              value={selectedType}
              onChange={(_, newValue) => {
                setSelectedType(newValue as "activity" | "session")
              }}
            />
          </div>
        )}
        {!hideRadioSelection && selectedType === "activity" && (
          <div className="report-session-filter-activity-content">
            <div className="session-radio-label">
              <p>{t("activity")}</p>
              <p>{detail && getWholeDuration("activity", detail)}</p>
            </div>
          </div>
        )}
        {!hideRadioSelection && selectedType === "session" && !!detail?.sessions.length && (
          <MenuItem
            key="session-with-tab"
            value="session"
            className="report-session-filter-session-item"
            disableRipple
            sx={{ display: "block", padding: "0 !important" }}>
            <div className="report-session-filter-selector-group">
              <Checkbox
                className="report-session-filter-checkbox-all"
                value="session"
                label={renderRadioLabel("session")}
                checked={isAllSessionsSelected}
                indeterminate={tempSelectedSessionList.length > 0 && !isAllSessionsSelected}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSelectAllSessions(!isAllSessionsSelected)
                }}
                color={CheckboxColor.PLATINUM}
              />
              {renderSessionCheckboxList(false)}
            </div>
          </MenuItem>
        )}
        {hideRadioSelection && selectedType === "session" && !!detail?.sessions.length && (
          <MenuItem
            key="session-checkbox"
            value="session"
            className="report-session-filter-session-item"
            disableRipple
            sx={{ display: "block", padding: "0 !important" }}>
            <div className="report-session-filter-selector-group">
              <Checkbox
                className="report-session-filter-checkbox-all"
                value="session"
                label={renderRadioLabel("session")}
                checked={isAllSessionsSelected}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSelectAllSessions(!isAllSessionsSelected)
                }}
                color={CheckboxColor.PLATINUM}
              />
              {renderSessionCheckboxList(false)}
            </div>
          </MenuItem>
        )}
      </Selector>
    </div>
  )
}
